import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '../store/gameStore';
import { ArrowLeft, Timer, Zap } from 'lucide-react';

interface Firefly {
  id: number;
  x: number;
  y: number;
  caught: boolean;
  speed: number;
}

export default function FireflyChase() {
  const navigate = useNavigate();
  const { speedLevel, addEnergy, completeLevel, unlockLevel } = useGameStore();
  const [fireflies, setFireflies] = useState<Firefly[]>([]);
  const [score, setScore] = useState(0);
  const [caught, setCaught] = useState(0);
  const [gameState, setGameState] = useState<'ready' | 'playing' | 'complete'>('ready');
  const [timeLeft, setTimeLeft] = useState(30);
  const [reactionTimes, setReactionTimes] = useState<number[]>([]);
  const [showResult, setShowResult] = useState(false);
  const [lastSpawn, setLastSpawn] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const moveRef = useRef<NodeJS.Timeout | null>(null);

  const spawnFirefly = () => {
    const newFirefly: Firefly = {
      id: Date.now() + Math.random(),
      x: Math.random() * 80 + 10,
      y: Math.random() * 70 + 10,
      caught: false,
      speed: (Math.random() * 2 + 1) * speedLevel,
    };
    setFireflies(prev => [...prev.slice(-8), newFirefly]);
    setLastSpawn(Date.now());
  };

  const startGame = () => {
    setGameState('playing');
    setScore(0);
    setCaught(0);
    setTimeLeft(30);
    setReactionTimes([]);
    setFireflies([]);
    spawnFirefly();
  };

  useEffect(() => {
    if (gameState === 'playing') {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setGameState('complete');
            setShowResult(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Move fireflies and spawn new ones
      moveRef.current = setInterval(() => {
        setFireflies(prev => {
          const moved = prev.map(f => f.caught ? f : ({
            ...f,
            x: Math.max(5, Math.min(90, f.x + (Math.random() - 0.5) * 12 * f.speed)),
            y: Math.max(5, Math.min(85, f.y + (Math.random() - 0.5) * 12 * f.speed)),
          }));
          return moved;
        });

        // Spawn new firefly periodically
        if (Math.random() < 0.3) {
          spawnFirefly();
        }
      }, 600);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (moveRef.current) clearInterval(moveRef.current);
    };
  }, [gameState, speedLevel]);

  const catchFirefly = (id: number) => {
    if (gameState !== 'playing') return;
    const reaction = Date.now() - lastSpawn;
    setReactionTimes(prev => [...prev, reaction]);
    setFireflies(prev => prev.map(f => f.id === id ? { ...f, caught: true } : f));
    setScore(s => s + 20);
    setCaught(c => c + 1);
    
    // Spawn a new one
    setTimeout(spawnFirefly, 300);
  };

  const handleComplete = () => {
    const avgReaction = reactionTimes.length > 0
      ? reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length
      : 0;
    const totalFlies = fireflies.length;
    const accuracy = totalFlies > 0 ? (caught / totalFlies) * 100 : 0;

    const energyEarned = Math.floor(score / 5);
    addEnergy(energyEarned);

    completeLevel({
      levelId: 'firefly-chase',
      levelName: '萤火虫追踪',
      score,
      accuracy: Math.min(100, accuracy),
      reactionTime: avgReaction,
      completedAt: new Date().toISOString(),
      contrast: 1,
      speed: speedLevel,
    });

    unlockLevel('memory-puzzle');
    navigate('/report');
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-[#050d08] via-[#0a1a10] to-[#0d2015] relative overflow-hidden">
      {/* Stars background */}
      <div className="absolute inset-0">
        {Array.from({ length: 30 }).map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white/20 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
            }}
          />
        ))}
      </div>

      {/* Header */}
      <header className="relative z-10 px-4 pt-6 pb-2">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <button onClick={() => navigate('/')} className="text-white/60 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-base font-bold text-white">✨ 萤火虫追踪</h1>
          <div className="flex items-center gap-2">
            <Timer className="w-4 h-4 text-white/60" />
            <span className={`text-sm font-bold ${timeLeft <= 10 ? 'text-red-400' : 'text-white/80'}`}>{timeLeft}s</span>
          </div>
        </div>
        <div className="max-w-lg mx-auto mt-3 flex items-center justify-between text-sm">
          <span className="text-[#fbbf24]">⚡ {score}分</span>
          <span className="text-[#7dffa8]">🪲 捕获: {caught}</span>
        </div>
      </header>

      {/* Game Area */}
      <div className="relative z-10 px-4 mt-4">
        <div className="max-w-lg mx-auto">
          {gameState === 'ready' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              <motion.div
                className="text-6xl mb-6"
                animate={{ y: [0, -10, 0], rotate: [0, 5, -5, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                ✨
              </motion.div>
              <h2 className="text-xl font-bold text-white mb-3">萤火虫追踪</h2>
              <p className="text-white/60 text-sm mb-2 max-w-xs mx-auto">
                在夜空中追踪并捕捉飞舞的萤火虫！
              </p>
              <p className="text-white/60 text-sm mb-8 max-w-xs mx-auto">
                训练眼球多维度转动，强化视觉追踪能力。
              </p>
              <button onClick={startGame} className="btn-primary text-lg">
                <Zap className="w-5 h-5 inline mr-2" />开始捕捉
              </button>
            </motion.div>
          )}

          {gameState === 'playing' && (
            <div className="relative w-full aspect-[3/4] max-w-[400px] mx-auto rounded-2xl border border-[#2d5a3f]/30 bg-black/40 overflow-hidden">
              {/* Grass at bottom */}
              <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-[#1a3525] to-transparent" />
              
              <AnimatePresence>
                {fireflies.map(f => !f.caught && (
                  <motion.button
                    key={f.id}
                    className="absolute -translate-x-1/2 -translate-y-1/2"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{
                      scale: [1, 1.3, 1],
                      opacity: [0.7, 1, 0.7],
                      left: `${f.x}%`,
                      top: `${f.y}%`,
                    }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{
                      scale: { duration: 1.5, repeat: Infinity },
                      opacity: { duration: 1.5, repeat: Infinity },
                      left: { type: 'spring', stiffness: 80 },
                      top: { type: 'spring', stiffness: 80 },
                    }}
                    onClick={() => catchFirefly(f.id)}
                  >
                    <div
                      className="w-8 h-8 rounded-full"
                      style={{
                        background: 'radial-gradient(circle, #fbbf24, #f59e0b, transparent)',
                        boxShadow: '0 0 20px #fbbf24, 0 0 40px rgba(251,191,36,0.4)',
                      }}
                    />
                  </motion.button>
                ))}
              </AnimatePresence>

              {/* Caught animation */}
              <AnimatePresence>
                {fireflies.filter(f => f.caught).slice(-3).map(f => (
                  <motion.div
                    key={`caught-${f.id}`}
                    className="absolute text-lg pointer-events-none"
                    style={{ left: `${f.x}%`, top: `${f.y}%` }}
                    initial={{ scale: 1, opacity: 1 }}
                    animate={{ scale: 2, opacity: 0, y: -30 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    +20
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>

      {/* Result Modal */}
      <AnimatePresence>
        {showResult && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              className="game-card p-6 max-w-sm w-full text-center"
            >
              <div className="text-4xl mb-4">🌟</div>
              <h2 className="text-xl font-bold text-white mb-4">捕捉完成！</h2>
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-xs text-white/50">得分</p>
                  <p className="text-lg font-bold text-[#7dffa8]">{score}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-xs text-white/50">捕获数量</p>
                  <p className="text-lg font-bold text-[#fbbf24]">{caught}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-xs text-white/50">平均反应</p>
                  <p className="text-lg font-bold text-[#60a5fa]">
                    {reactionTimes.length > 0 ? Math.round(reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length) : 0}ms
                  </p>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-xs text-white/50">获得能量</p>
                  <p className="text-lg font-bold text-[#fbbf24]">+{Math.floor(score / 5)}</p>
                </div>
              </div>
              <button onClick={handleComplete} className="btn-primary w-full">
                查看训练报告 📊
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
